import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import { BILLING_LIST_PAGE_SIZE } from "../constants/pagination";
import {
  BillingAvailability,
  CreateBillingInvoiceDownloadDocument,
  CreateBillingInvoiceDownloadMutation,
  CreateBillingInvoiceDownloadMutationVariables,
  OrganizationBillingDocument,
  OrganizationBillingQuery,
  OrganizationBillingQueryVariables,
} from "../generated/graphql";
import { useBillingInvoiceDownload } from "../hooks/useBillingInvoiceDownload";
import { client } from "../lib/apolloClient";
import { captureException } from "../lib/errorHandling";
import { borderRadius, color, shadows, space } from "../theme/designTokens";
import { LoadStatus } from "../types/loadStatus";
import { CursorPagination } from "./CursorPagination";
import { OrganizationContractsSection } from "./OrganizationContractsSection";

type BillingState =
  | { status: LoadStatus.Loading | LoadStatus.Error }
  | { status: LoadStatus.Ready; billing: OrganizationBillingQuery["organizationBilling"] };

type OrganizationBillingSectionProps = {
  organizationId?: string;
};

export const OrganizationBillingSection = ({ organizationId }: OrganizationBillingSectionProps) => {
  const translate = useTranslate();
  const [state, setState] = useState<BillingState>({ status: LoadStatus.Loading });
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [reload, setReload] = useState(0);
  const { downloadingId, downloadError, readyDownload, download, reset } = useBillingInvoiceDownload(
    async invoiceId => {
      const result = await client.mutate<
        CreateBillingInvoiceDownloadMutation,
        CreateBillingInvoiceDownloadMutationVariables
      >({
        mutation: CreateBillingInvoiceDownloadDocument,
        variables: organizationId ? { invoiceId, organizationId } : { invoiceId },
        fetchPolicy: "no-cache",
      });
      return result.data?.createBillingInvoiceDownload;
    }
  );
  const after = cursors[cursors.length - 1];

  useEffect(() => {
    let active = true;
    const load = async () => {
      setState({ status: LoadStatus.Loading });
      reset();
      try {
        const result = await client.query<OrganizationBillingQuery, OrganizationBillingQueryVariables>({
          query: OrganizationBillingDocument,
          variables: organizationId ? { after, organizationId } : { after },
          fetchPolicy: "no-cache",
          context: { queryDeduplication: false },
        });
        if (active) {
          const billing = result.data.organizationBilling;
          if (
            after !== null &&
            billing.availability === BillingAvailability.Available &&
            billing.invoices.length === 0
          ) {
            setCursors(value => value.slice(0, -1));
            return;
          }
          setState({ status: LoadStatus.Ready, billing });
        }
      } catch (error) {
        captureException(error, "billing/invoices: load organisation billing");
        if (active) setState({ status: LoadStatus.Error });
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [after, organizationId, reload, reset]);

  const billing = state.status === LoadStatus.Ready ? state.billing : null;
  const busy = state.status === LoadStatus.Loading || downloadingId !== null;

  return (
    <Box component="section" aria-labelledby="billing-heading" data-testid="organization-billing">
      <Typography id="billing-heading" variant="h6" mb={space.xs}>
        {translate("billing.title")}
      </Typography>
      <OrganizationContractsSection organizationId={organizationId} />
      <Paper
        data-testid="organization-invoices"
        variant="outlined"
        sx={{
          borderColor: color.panelBorder,
          borderRadius: borderRadius.panel,
          boxShadow: shadows.card,
          overflow: "hidden",
        }}
      >
        <Stack gap={space.sm} p={space.sm2}>
          {state.status === LoadStatus.Loading && (
            <Box role="status" aria-label={translate("billing.loading")}>
              <Skeleton width="60%" />
              <Skeleton height={56} />
              <Skeleton height={56} />
            </Box>
          )}
          {state.status === LoadStatus.Error && (
            <Alert
              severity="error"
              action={<Button onClick={() => setReload(value => value + 1)}>{translate("billing.retry")}</Button>}
            >
              {translate("billing.loadError")}
            </Alert>
          )}
          {billing && (
            <>
              <Typography color="text.secondary">
                {translate("billing.description", {
                  organization: billing.organizationName || translate("account.notAvailable"),
                })}
              </Typography>
              {billing.availability === BillingAvailability.Disabled ? (
                <Alert severity="info">{translate("billing.accessDisabled")}</Alert>
              ) : billing.availability === BillingAvailability.NotConfigured ? (
                <Alert severity="info">{translate("billing.notConfigured")}</Alert>
              ) : (
                <>
                  {billing.invoices.length === 0 ? (
                    <Typography>{translate("billing.empty")}</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small" aria-label={translate("billing.documents")}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{translate("billing.number")}</TableCell>
                            <TableCell>{translate("billing.date")}</TableCell>
                            <TableCell align="right">{translate("billing.amount")}</TableCell>
                            <TableCell align="right">{translate("billing.document")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {billing.invoices.map(invoice => (
                            <TableRow key={invoice.id}>
                              <TableCell component="th" scope="row">
                                {invoice.number || translate("account.notAvailable")}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {new Intl.DateTimeFormat("de-DE", {
                                  timeZone: "UTC",
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }).format(new Date(invoice.documentDate))}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {new Intl.NumberFormat("de-DE", {
                                  style: "currency",
                                  currency: invoice.currency,
                                }).format(Number(invoice.totalGross))}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  disabled={downloadingId !== null}
                                  startIcon={
                                    downloadingId === invoice.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DownloadOutlinedIcon />
                                    )
                                  }
                                  aria-label={translate("billing.downloadLabel", {
                                    number: invoice.number || invoice.documentDate,
                                  })}
                                  onClick={() => void download(invoice.id)}
                                >
                                  {translate(downloadingId === invoice.id ? "billing.downloading" : "billing.download")}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {downloadError && <Alert severity="error">{translate("billing.downloadError")}</Alert>}
                  {readyDownload && (
                    <Alert severity="info">
                      {translate("billing.popupBlocked")}{" "}
                      <Button
                        component="a"
                        href={readyDownload.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                      >
                        {translate("billing.openDocument")}
                      </Button>
                    </Alert>
                  )}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={space.xs}
                  >
                    <Button
                      startIcon={<RefreshOutlinedIcon />}
                      disabled={busy}
                      onClick={() => {
                        setCursors([null]);
                        setReload(value => value + 1);
                      }}
                    >
                      {translate("billing.refresh")}
                    </Button>
                    <CursorPagination
                      page={cursors.length}
                      perPage={BILLING_LIST_PAGE_SIZE}
                      itemCount={billing.invoices.length}
                      hasNextPage={Boolean(billing.nextCursor)}
                      isPending={busy}
                      resource="OrganizationInvoice"
                      onPageChange={page => {
                        if (page >= 1 && page < cursors.length) setCursors(value => value.slice(0, page));
                        const nextCursor = billing.nextCursor;
                        if (page === cursors.length + 1 && nextCursor) {
                          setCursors(value => [...value, nextCursor]);
                        }
                      }}
                    />
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
